import React, { useEffect, useRef, useState } from "react";
import Navbar from "./components/Navbar";
import YourPersonalTripPlanner from "./components/YourPersonalTripPlanner";
import Benefits from "./components/Benefits";
import OurCoverage from "./components/OurCoverage";
// import Testimonials from "./components/Testimonials";
import GetStarted from "./components/GetStarted";
import Footer from "./components/Footer";
import useTranslate from "../../hooks/useTranslate";
import { SvgIcons } from "@tripian/react";
import { useNumberCounter } from "./useNumberCounter";
import { useWhenVisible } from "./useWhenVisible";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ICombinedState from "../../redux/model/ICombinedState";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  LANDING,
  LOGIN,
  REGISTER,
  TRIPS,
} from "../../constants/ROUTER_PATH_TITLE";

const counterSecond = 2;
const counterTimes = 20;

const Landing = () => {
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { language } = useParams();
  const formattedLanguage = language
    ? language.toUpperCase() === "PT-BR"
      ? "pt-BR"
      : language.split("-")[0].toLowerCase()
    : undefined;

  const { t, onSelectedLangCode, langCode } = useTranslate();

  useEffect(() => {
    const langCodeKeys: string[] = window.tconfig.T.langCodes.map(l => l.value);

    if (formattedLanguage && langCodeKeys.includes(formattedLanguage)) {
      onSelectedLangCode(formattedLanguage);
    }
  }, [formattedLanguage, onSelectedLangCode]);

  const { count: countriesCount, runCounter: runCountriesCounter } =
    useNumberCounter(65, counterSecond, counterTimes);
  const { count: citiesCount, runCounter: runCitiesCounter } = useNumberCounter(
    550,
    counterSecond,
    counterTimes
  );
  const { refDom } = useWhenVisible(() => {
    runCountriesCounter();
    runCitiesCounter();
  });

  const isLoggedIn = useSelector(
    (state: ICombinedState) => state.user.isLoggedIn
  );

  const { getLocalData } = useLocalStorage<"light" | "dark">("theme");
  const theme = getLocalData();

  const navigate = useNavigate();

  document.title = LANDING.TITLE(t("welcome"));

  const onClickPlanYourTrip = () => {
    if (isLoggedIn) {
      navigate(TRIPS.PATH);
    } else {
      navigate(LOGIN.PATH);
    }
  };

  const onClickSignUp = () => {
    if (isLoggedIn) {
      navigate(TRIPS.PATH);
    } else {
      if (window.tconfig.SHOW_REGISTER) {
        navigate(REGISTER.PATH);
      } else {
        onClickPlanYourTrip();
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLanguageSelect = (langCode: string) => {
    onSelectedLangCode(langCode);
    setIsLanguageDropdownOpen(false);
    navigate(LANDING.PATH, { replace: true, preventScrollReset: true });
  };

  return (
    <>
      {window.tconfig.LANDING_PAGE_URL ? (
        <iframe
          className="w-full h-full"
          src={window.tconfig.LANDING_PAGE_URL}
          title={window.tconfig.BRAND_NAME}
        ></iframe>
      ) : (
        <div className="min-h-screen font-geist text-[#0B1215]">
          <Navbar
            theme={theme}
            isLoggedIn={isLoggedIn}
            onClickSignUp={onClickSignUp}
            onClickPlanYourTrip={onClickPlanYourTrip}
            t={t}
          />
          <div
            className="absolute top-[6.5rem] end-4 md:end-6 z-10"
            ref={dropdownRef}
          >
            <button
              onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
              className="flex items-center gap-2 bg-white bg-opacity-10 backdrop-blur-sm text-white px-4 py-2.5 rounded-full hover:bg-opacity-20 transition-all duration-200 border border-white border-opacity-20 min-w-[100px]"
            >
              <SvgIcons.Globe />
              <span className="font-medium">
                {langCode.toLocaleUpperCase()}
              </span>
              <SvgIcons.ChevronDown
                size="1rem"
                className={`transform transition-transform duration-200 ${isLanguageDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isLanguageDropdownOpen && (
              <div className="absolute top-full end-0 mt-2 w-60 bg-white rounded-3xl shadow-xl border border-gray-100 z-50">
                {window.tconfig.T.langCodes.map((language, index) => (
                  <button
                    key={language.value}
                    onClick={() => handleLanguageSelect(language.value)}
                    className={`w-full text-start px-4 py-2.5 hover:bg-primary-color-50 transition-colors duration-150 flex items-center justify-between
                      ${langCode === language.value ? "bg-primary-color-50 text-primary-color" : "text-gray-700"}
                      ${index === 0 ? "rounded-t-3xl" : ""}
                      ${index === window.tconfig.T.langCodes.length - 1 ? "rounded-b-3xl" : ""}`}
                  >
                    <span className="font-medium">{language.label}</span>
                    <span className="text-sm text-gray-500">
                      {language.value.toLocaleUpperCase()}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <main>
            <YourPersonalTripPlanner
              onClickGetStarted={onClickPlanYourTrip}
              t={t}
            />
            <Benefits t={t} />
            <OurCoverage
              countriesCount={countriesCount}
              citiesCount={citiesCount}
              refDom={refDom}
              t={t}
            />
            {/* <Testimonials t={t} /> */}
            <div className="py-24" />
            <GetStarted t={t} />
            <div className="py-36 text-center">
              <h2 className="text-[1.75rem] md:text-5xl/[100%] font-semibold md:font-bold mb-10">
                {t("landing.readyToGetStarted")}
              </h2>
              <button
                className="bg-primary-color text-white font-semibold px-9 py-4 rounded-full transition text-xs md:text-base"
                onClick={onClickPlanYourTrip}
              >
                {t("landing.planYourTrip")}
              </button>
            </div>
          </main>
          <Footer t={t} />
        </div>
      )}
    </>
  );
};

export default Landing;
